import { useState } from 'react';
import { AlertTriangle, Check, Copy, KeyRound, RotateCw, Trash2 } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { toast } from '@/stores/toast-store';
import {
  useCreateEslApiClient,
  useEslApiClients,
  useRevokeEslApiClient,
  useRotateEslApiClient,
  type CreatedEslClient,
} from '@/hooks/use-esl';

// Super-admin only, on purpose.
//
// Issuing a credential mints a caller that reaches our API and writes to real
// shelves. That is a platform decision — support issues it and hands it to the
// POS vendor — not something a business does for itself. The ESL service
// enforces the same rule: the endpoints refuse anyone who is not a platform
// admin, so removing this from the tenant portal is not the only thing stopping
// a tenant admin from calling them directly.

const SCOPE_OPTIONS = [
  { key: 'esl:write', label: 'Update prices', hint: 'Send item data' },
  { key: 'esl:read', label: 'Read status', hint: 'Labels, events, health' },
  { key: 'esl:bind', label: 'Link labels', hint: 'Change which product a label shows' },
  { key: 'esl:flash', label: 'Locate labels', hint: 'Flash a label on the shelf' },
];

interface EslApiCredentialsProps {
  tenantId: string;
  locations: Array<{ id: string; name: string }>;
}

export function EslApiCredentials({ tenantId, locations }: EslApiCredentialsProps) {
  const { data: clients, isLoading } = useEslApiClients(tenantId);
  const create = useCreateEslApiClient(tenantId);
  const revoke = useRevokeEslApiClient(tenantId);
  const rotate = useRotateEslApiClient(tenantId);

  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<string[]>(['esl:write', 'esl:read']);
  const [env, setEnv] = useState<'test' | 'live'>('test');
  const [pinnedLocation, setPinnedLocation] = useState<string>(locations[0]?.id ?? '');
  const [created, setCreated] = useState<CreatedEslClient | null>(null);
  const [rotated, setRotated] = useState<CreatedEslClient | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const message = (e: unknown, fallback: string) => (e as { error?: { message?: string } })?.error?.message || fallback;

  return (
    <div className="rounded-lg border border-border bg-white p-4">
      <div className="mb-1 flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-primary" />
        <h3 className="font-medium">Shelf Labels API credentials</h3>
      </div>
      <p className="mb-4 text-xs text-muted-foreground">
        Issue these on the business&apos;s behalf and pass them to whoever sends their prices — their POS vendor, or
        their own developers. They authenticate against our API and never touch the label platform. The business cannot
        create these itself.
      </p>

      <div className="mb-4 rounded-md border border-border p-3">
        <label className="mb-1 block text-sm font-medium">Who is this for?</label>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Acme POS integration"
          className="mb-3 w-full rounded-md border border-border p-2 text-sm"
        />

        <p className="mb-2 text-sm font-medium">What may they do?</p>
        <div className="mb-3 space-y-1.5">
          {SCOPE_OPTIONS.map((s) => (
            <label key={s.key} className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={scopes.includes(s.key)}
                onChange={(e) =>
                  setScopes((prev) => (e.target.checked ? [...prev, s.key] : prev.filter((x) => x !== s.key)))
                }
                className="mt-1"
              />
              <span>
                <span className="font-medium">{s.label}</span>
                <span className="ml-2 text-muted-foreground">{s.hint}</span>
              </span>
            </label>
          ))}
        </div>

        <p className="mb-2 text-sm font-medium">Environment</p>
        <div className="mb-3 flex gap-2">
          {(['test', 'live'] as const).map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => setEnv(e)}
              className={`flex-1 rounded-md border-2 p-2 text-left text-sm ${
                env === e ? 'border-primary bg-primary/5' : 'border-border'
              }`}
            >
              <span className="font-medium">{e === 'test' ? 'Test' : 'Live'}</span>
              <span className="ml-2 text-xs text-muted-foreground">
                {e === 'test' ? 'for their integration work' : 'writes to real shelves'}
              </span>
            </button>
          ))}
        </div>

        <label className="mb-1 block text-sm font-medium">Scope</label>
        <select
          value={pinnedLocation}
          onChange={(e) => setPinnedLocation(e.target.value)}
          className="mb-1 w-full rounded-md border border-border p-2 text-sm"
        >
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} only
            </option>
          ))}
          <option value="">All of this business&apos;s stores</option>
        </select>
        <p className="mb-3 text-xs text-muted-foreground">
          Pin it to one store unless the integration genuinely covers all of them — a pinned credential cannot reach the
          others.
        </p>

        <button
          disabled={!label.trim() || !scopes.length || create.isPending}
          onClick={async () => {
            try {
              setCreated(
                await create.mutateAsync({
                  label: label.trim(),
                  scopes,
                  env,
                  locationId: pinnedLocation || null,
                }),
              );
              setLabel('');
            } catch (e) {
              toast.error(message(e, 'Could not create the credential'));
            }
          }}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          <KeyRound className="h-4 w-4" /> Create credential
        </button>
      </div>

      {created && <SecretOnce created={created} onDismiss={() => setCreated(null)} />}
      {rotated && <SecretOnce created={rotated} rotated onDismiss={() => setRotated(null)} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !clients?.length ? (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No credentials issued for this business yet.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40 text-left">
              <tr>
                <th className="p-2 font-medium">Label</th>
                <th className="p-2 font-medium">Client ID</th>
                <th className="p-2 font-medium">Env</th>
                <th className="p-2 font-medium">Permissions</th>
                <th className="p-2 font-medium">Scope</th>
                <th className="p-2 font-medium">Last used</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0">
                  <td className="p-2 font-medium">{c.label ?? '—'}</td>
                  <td className="p-2 font-mono text-[11px]">{c.clientId}</td>
                  <td className="p-2">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[11px] ${
                        c.env === 'live' ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {c.env}
                    </span>
                  </td>
                  <td className="p-2 text-[11px]">{c.scopes.join(', ')}</td>
                  <td className="p-2 text-[11px]">
                    {c.locationId ? (locations.find((l) => l.id === c.locationId)?.name ?? 'One store') : 'All stores'}
                  </td>
                  <td className="p-2 text-[11px] text-muted-foreground">
                    {c.lastUsedAt ? new Date(c.lastUsedAt).toLocaleString() : 'Never'}
                  </td>
                  <td className="p-2 text-right">
                    {c.status === 'revoked' ? (
                      <span className="text-[11px] text-muted-foreground">Revoked</span>
                    ) : (
                      <>
                        {/* Rotating changes ONE value. Revoke-and-reissue changes
                            two and leaves a window where neither works. */}
                        <button
                          onClick={async () => {
                            try {
                              setRotated(await rotate.mutateAsync(c.id));
                            } catch (e) {
                              toast.error(message(e, 'Could not rotate'));
                            }
                          }}
                          title="Issue a new secret for this same client ID"
                          className="mr-2 inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px]"
                        >
                          <RotateCw className="h-3 w-3" /> Rotate
                        </button>
                        <button
                          onClick={() => setRevoking(c.id)}
                          className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-red-600"
                        >
                          <Trash2 className="h-3 w-3" /> Revoke
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!revoking}
        title="Revoke this credential?"
        message="Any system using it stops being able to update prices immediately. This cannot be undone."
        confirmLabel="Revoke"
        loading={revoke.isPending}
        onConfirm={async () => {
          if (!revoking) return;
          try {
            await revoke.mutateAsync(revoking);
            toast.success('Credential revoked');
          } catch (e) {
            toast.error(message(e, 'Could not revoke'));
          }
          setRevoking(null);
        }}
        onCancel={() => setRevoking(null)}
      />
    </div>
  );
}

// The secret exists in exactly one place for exactly one moment. Only a hash is
// stored, so it cannot be shown again — losing it means rotating.
function SecretOnce({
  created,
  onDismiss,
  rotated = false,
}: {
  created: CreatedEslClient;
  onDismiss: () => void;
  rotated?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <div className="mb-4 rounded-lg border-2 border-amber-300 bg-amber-50 p-4">
      <div className="mb-3 flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div>
          <p className="font-medium text-amber-900">Copy this secret now — it will not be shown again</p>
          <p className="text-sm text-amber-800">
            Only a hashed copy is stored. If it is lost, rotate the credential to get a new one.
            {rotated && ' The previous secret stopped working the moment this one was issued.'}
          </p>
        </div>
      </div>

      <div className="mb-3 space-y-1 rounded-md bg-white p-3 font-mono text-xs">
        <div>
          <span className="text-muted-foreground">client_id</span>
          <br />
          {created.clientId}
        </div>
        <div className="pt-2">
          <span className="text-muted-foreground">client_secret</span>
          <br />
          {created.clientSecret}
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={async () => {
            await navigator.clipboard.writeText(`client_id=${created.clientId}\nclient_secret=${created.clientSecret}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          }}
          className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-3 py-2 text-sm font-medium text-white"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? 'Copied' : 'Copy both'}
        </button>
        <button onClick={onDismiss} className="rounded-md border border-amber-300 px-3 py-2 text-sm">
          I have saved it
        </button>
      </div>
    </div>
  );
}
